import { parseDateFlexible, formatDate } from "@/lib/dates";

// Stacked horizontal bar chart that shows the date span of each project in a folder.
// Non-adjustable: it's a *summary* view — sorting and clicking happens inside the folder.
// Projects without dates show a ghost row so users still see "N projects, but no dates yet."
//
// Props:
//   projects: [{ id, name, start_date, go_live_date }]
//   min_start, max_end: (optional) global bounds for the folder; otherwise derived.
//   palette: array of hex colours (defaults to the app palette).
//   maxRows: cap the visible bars (rest rolled up into a "+N more" hint).
export default function FolderSummaryChart({
  projects = [],
  minStart,
  maxEnd,
  palette = ["#241623", "#7DAA92", "#63ADF2", "#ea580c", "#9333ea", "#0891b2", "#be185d"],
  maxRows = 5,
}) {
  // Compute overall bounds
  let lo = minStart ? parseDateFlexible(minStart) : null;
  let hi = maxEnd ? parseDateFlexible(maxEnd) : null;
  if (!lo || !hi) {
    for (const p of projects) {
      const s = parseDateFlexible(p.start_date);
      const e = parseDateFlexible(p.go_live_date);
      if (s && (!lo || s < lo)) lo = s;
      if (e && (!hi || e > hi)) hi = e;
    }
  }
  const hasBounds = lo && hi && hi.getTime() > lo.getTime();
  const totalMs = hasBounds ? hi.getTime() - lo.getTime() : 0;

  const shown = projects.slice(0, maxRows);
  const extra = projects.length - shown.length;

  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 p-3" data-testid="folder-summary-chart">
      {/* Time axis */}
      <div className="flex items-center justify-between text-[9px] font-mono-ibm text-slate-500 uppercase tracking-widest mb-2">
        <span data-testid="summary-axis-start">{hasBounds ? formatDate(lo) : "—"}</span>
        <span className="text-slate-400">Timeline span</span>
        <span data-testid="summary-axis-end">{hasBounds ? formatDate(hi) : "—"}</span>
      </div>

      {/* Bars */}
      {shown.length === 0 ? (
        <div className="text-[11px] text-slate-400 text-center py-4">
          No projects in this folder yet.
        </div>
      ) : (
        <div className="space-y-1" data-testid="summary-bars">
          {shown.map((p, idx) => {
            const color = palette[idx % palette.length];
            const s = parseDateFlexible(p.start_date);
            const e = parseDateFlexible(p.go_live_date);
            const ok = hasBounds && s && e && e.getTime() >= s.getTime();
            const left = ok ? ((s.getTime() - lo.getTime()) / totalMs) * 100 : 0;
            let width = ok ? ((e.getTime() - s.getTime()) / totalMs) * 100 : 100;
            // Guarantee a tiny visible bar for zero-duration projects so the row isn't "empty"
            if (ok && width < 1.5) width = 1.5;
            return (
              <div
                key={p.id}
                className="relative h-4 bg-white border border-slate-200 rounded-[3px] overflow-hidden"
                title={`${p.name} — ${p.start_date || "?"} → ${p.go_live_date || "?"}`}
                data-testid={`summary-bar-${p.id}`}
              >
                {ok ? (
                  <div
                    className="absolute top-0 bottom-0 rounded-[2px]"
                    style={{ left: `${left}%`, width: `${width}%`, background: color }}
                  />
                ) : (
                  // Dashed, zero-opacity placeholder so the row isn't blank when dates are missing.
                  <div
                    className="absolute inset-0 bg-[length:8px_8px] opacity-40"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, #cbd5e1 0 3px, transparent 3px 8px)",
                    }}
                  />
                )}
                <div
                  className="absolute inset-0 flex items-center px-2 text-[10px] font-medium text-slate-700 pointer-events-none truncate"
                >
                  <span className="relative z-10 truncate" style={{ color: ok && width > 45 ? "white" : undefined }}>
                    {p.name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {extra > 0 && (
        <div className="mt-2 text-[10px] font-mono-ibm text-slate-500 uppercase tracking-widest text-right">
          +{extra} more inside
        </div>
      )}
    </div>
  );
}
